/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { ListagemPagamento } from './../../../models/listagem-pagamento'
import { AfterViewInit, Component, OnInit, ViewChild, DestroyRef, inject } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { fromEvent, debounceTime, distinctUntilChanged, tap } from 'rxjs'
import { filter } from 'rxjs/operators'
import { ListagensDiversasService } from '../../../services/listagens-diversas.service'
import { ActivatedRoute } from '@angular/router'
import { DatePipe } from '@angular/common'
import { FormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { ArquivoViewerDialogComponent } from '../../arquivo-viewer-dialog/arquivo-viewer-dialog.component'
import { DownloadService } from '../../../services/download.service'
import { UnespCoreAuthService, UnespCoreMessageService } from 'src/libs/unesp-core'
import { InscricaoPagamentoService } from '../../../services/inscricao-pagamento.service'
import { ConcursoService } from 'src/app/services/concurso.service'
import { SearchInputComponent, DefaultPaginatorComponent } from 'src/app/modules/shared'
import { MaskUtils } from 'src/app/utils/mask.utils'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-pagamento',
  templateUrl: './pagamento.component.html',
  styleUrls: ['./pagamento.component.scss'],
})
export class PagamentoComponent implements AfterViewInit, OnInit {
  private destroyRef = inject(DestroyRef)

  @ViewChild('paginator', { static: true }) paginator!: DefaultPaginatorComponent
  @ViewChild(MatSort) sort!: MatSort
  @ViewChild('searchInput') searchInput!: SearchInputComponent

  totalElements: number = 0
  sortField = 'id'
  sortDirection = 'desc'
  colunasGrid: string[] = [
    'id',
    'nome',
    'cpf',
    'protocolo',
    'valor',
    'actionComprovante',
    'pago',
    'autor',
    'data',
    'concluida',
    'action',
  ]
  dataSource: MatTableDataSource<ListagemPagamento> = new MatTableDataSource()
  pagamentosAtualizados: ListagemPagamento[] = []
  pagamentoListado: FormControl = new FormControl('')
  idConcurso?: number
  concursoTitulo?: string
  filtro: string = ''
  datepipe: DatePipe = new DatePipe('pt-BR')

  constructor(
    private listagensDiversasService: ListagensDiversasService,
    private route: ActivatedRoute,
    private unespCoreMessageService: UnespCoreMessageService,
    private dialog: MatDialog,
    private downloadService: DownloadService,
    private inscricaoPagamentoService: InscricaoPagamentoService,
    private readonly concursoService: ConcursoService,
    private unespCoreAuthService: UnespCoreAuthService
  ) {}

  ngOnInit(): void {
    this.paginator.pageSize = 10
    ;(this.route.parent?.params || this.route.params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.idConcurso = params['id']
      this.getData()
    })
  }

  ngAfterViewInit() {
    // server-side search
    fromEvent<KeyboardEvent>(this.searchInput.inputRef.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        filter((e: KeyboardEvent) => e.keyCode === 13),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0
          this.getData()
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe()

    // on sort change, reset to first page and reload
    this.sort.sortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.sortField = this.sort.active
      this.sortDirection = this.sort.direction || 'asc'
      this.paginator.pageIndex = 0
      this.getData()
    })

    // on paginate events, load a new page
    this.paginator?.page.pipe(tap(() => this.getData()), takeUntilDestroyed(this.destroyRef)).subscribe()
  }

  private getData() {
    this.listagensDiversasService
      .getPagamento(
        this.idConcurso!,
        this.searchInput?.value,
        this.filtro,
        this.paginator.pageIndex,
        this.paginator.pageSize,
        this.sortField,
        this.sortDirection
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.dataSource = new MatTableDataSource(data['content'])
        this.totalElements = data['totalElements']
        if (data['content'].length != 0) {
          this.concursoTitulo = data['content'][0].concursoTitulo
        } else {
          this.concursoService.getTitulo(this.idConcurso).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(resp => {
            this.concursoTitulo = resp.concursoTitulo
          })
        }
      })
  }

  exportar() {
    if(!this.isSuperAdmin()) {
      this.unespCoreMessageService.showMessageError(`Apenas administradores podem exportar dados`)
      return
    }
    this.listagensDiversasService
      .getPagamento(this.idConcurso!, this.searchInput?.value, this.filtro, 0, this.totalElements)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        let recursos: ListagemPagamento[] = data['content']
        let content = `"#","NOME","CPF","VALOR","PAGO","AUTOR","DATA","CONCLUIDA"\r\n`

        recursos.forEach(row => {
          content += `"${row.id}","${MaskUtils.preferSocialName(row.nome, row.nomeSocial)}","${MaskUtils.maskCpf(row.cpf)}","${row.valor}","${
            row.pago ? 'Sim' : 'Não'
          }","${row.autor == null ? '' : row.autor}","${
            row.data == null ? '' : this.datepipe.transform(row.data, 'dd/MM/YYYY HH:mm:ss')
          }","${row.concluida ? 'Sim' : 'Nao'}"\r\n`
        })
        let binaryData = []
        binaryData.push(content)
        let downloadLink = document.createElement('a')
        downloadLink.setAttribute('download', `listagem-pagamento-${this.idConcurso}.csv`)
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'attachment/csvcharset=utf-8' }))
        document.body.appendChild(downloadLink)
        downloadLink.click()
        downloadLink.remove()
      })
  }

  selecionar(): void {
    this.getData()
  }

  salvar(row: ListagemPagamento) {
    this.inscricaoPagamentoService.salvarEdicao(row.id, row.pago).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value) {
        row.pago = value.pago
        row.autor = value.pagoUsuario
        row.data = value.pagoData
        this.unespCoreMessageService.showMessageSuccess(`${row.nome}, registrado com sucesso`)
      } else {
        this.unespCoreMessageService.showMessageError(`Falha ao registrar, ${row.nome}`)
      }
    })
  }

  concluir(row: ListagemPagamento) {
    this.inscricaoPagamentoService.concluir(row.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value) {
        row.concluida = true
        this.unespCoreMessageService.showMessageSuccess(`${row.nome}, concluído com sucesso`)
      } else {
        this.unespCoreMessageService.showMessageError(`Falha ao concluir, ${row.nome}`)
      }
    })
  }

  verAnexo(caminho: string, anexo: string, titulo: string) {
    this.downloadService.arquivoObjectURLPorPath(caminho, anexo).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(url =>
      this.dialog.open(ArquivoViewerDialogComponent, {
        data: { tituloDoArquivo: titulo, urlDoArquivo: url },
        width: '80%',
        height: '90%',
        panelClass: 'fullheight-dialog',
      })
    )
  }

  isSuperAdmin(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['SUPERADMIN'])
  }
}
